import mitt from "mitt";

import type { AppSseEvent } from "@elation/api-client/src/stitch-web";

type SupportedAppSseEventType =
  | "encounter.snapshot"
  | "agenda_item.created"
  | "agenda_item.updated"
  | "agenda_item.deleted"
  | "agenda_items.reordered"
  | "encounter.finalizing"
  | "encounter.completed"
  | "encounter.coding_updated"
  | "encounter.reopened"
  | "encounter.superseded_by_reopen"
  | "encounter.transcription_created"
  | "transcript.snapshot"
  | "transcript.item.created"
  | "transcription.started"
  | "transcription.progress"
  | "transcription.succeeded"
  | "transcription.failed";

type AppBusSseEvent = Extract<AppSseEvent, { type: SupportedAppSseEventType }>;

/**
 * Payloads for events on the local `appBus`. Encounter SSE types mirror the
 * server so {@link useAppSSEEvents} can forward each frame with
 * `appBus.emit(event, data)` without translation. `client.*` entries are
 * emitted from the UI (e.g. patient layout) for routing-driven store updates.
 * `chrome.*` entries mirror extension background signals (see
 * {@link ../chrome/debugger/debuggerEventBus.ts}) once bridged from the service
 * worker into the extension UI bundle.
 */
export type AppBusEvents = {
  [K in AppBusSseEvent["type"]]: Extract<AppBusSseEvent, { type: K }>;
} & {
  "sse.fallback-polling": { isFallbackPolling: boolean };
  "client.router_params_changed": {
    params: Record<string, string | undefined>;
  };
  "client.router_search_params_changed": {
    searchParams: URLSearchParams;
    params: Record<string, string | undefined>;
  };
  "chrome.url_changed": { tabId: number; url: string };
  "chrome.network_request": { tabId: number; url: string; data: unknown };
  "chrome.toggle_assistant": { tabId: number };
};

export const appBus = mitt<AppBusEvents>();
